import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import socket from '../../../../utils/socket';
import { showConfirmAlert, showErrorAlert, showSuccessAlert } from '../../../../utils/alerts';
import { DEFAULT_MASTER_CONFIG, DEFAULT_SLAVE_CONFIG, DEFAULT_WIFI_CONFIG } from '../../../../constants/readerDefault';
import UnifiedConfigCard from './UnifiedConfigCard';
import './ReadersModule.css';

const ReaderConfig = ({
  buildingId,
  doors,
  selectedLocalId,
  setSelectedLocalId,
}) => {
  const [sending, setSending] = useState(false);
  const [masterConfig, setMasterConfig] = useState(DEFAULT_MASTER_CONFIG);
  const [slaveConfig, setSlaveConfig] = useState(DEFAULT_SLAVE_CONFIG);

  useEffect(() => {
    setMasterConfig(DEFAULT_MASTER_CONFIG);
    setSlaveConfig(DEFAULT_SLAVE_CONFIG);
  }, [buildingId]);

  useEffect(() => {
    setMasterConfig(DEFAULT_MASTER_CONFIG);
    setSlaveConfig(DEFAULT_SLAVE_CONFIG);
  }, [selectedLocalId]);

  const emitReaderConfig = ({ buildingId, localId, config, doorName }) => {
    const cleanup = () => {
      socket.off('reader:config:success', handleSuccess);
      socket.off('reader:config:error', handleError);
    };

    const handleSuccess = () => {
      cleanup();
      setSending(false);
      setSelectedLocalId('');
      showSuccessAlert(
        'Configuración enviada',
        `La lectora ${doorName} (ID: ${localId}) recibió la configuración correctamente.`
      );
    };

    const handleError = ({ message }) => {
      cleanup();
      setSending(false);
      showErrorAlert(
        'Error',
        message || 'No se pudo enviar la configuración.'
      );
    };

    socket.once('reader:config:success', handleSuccess);
    socket.once('reader:config:error', handleError);

    socket.emit('frontend:reader:config', { buildingId, localId, config });
  };


  const handleSend = (targetType, configToSend) => {
    if (!selectedLocalId) {
      showErrorAlert('Error', 'Selecciona una lectora.');
      return;
    }

    const door = doors.find(
      (d) => String(d.localId) === String(selectedLocalId)
    );

    if (!door) {
      showErrorAlert(
        'Error',
        'La lectora seleccionada no es válida para este edificio.'
      );
      return;
    }

    const targetLabel = targetType === 'master' ? 'Maestra' : 'Esclava';

    showConfirmAlert({
      title: 'Enviar configuración',
      message: `Se enviará la configuración de ${targetLabel} a ${door.name || 'Lectora'} (ID: ${selectedLocalId}).`,
      cancelButtonText: 'Cancelar',
      confirmText: 'Enviar',
      onConfirm: () => {
        setSending(true);
        emitReaderConfig({
          buildingId,
          localId: selectedLocalId,
          config: configToSend,
          doorName: door.name || `Lectora ${selectedLocalId}`,
        });
      },
    });
  };

  const handleSendWifi = (ssid) => {
    if (!selectedLocalId) {
      showErrorAlert('Error', 'Selecciona una lectora.');
      return;
    }

    const door = doors.find(
      (d) => String(d.localId) === String(selectedLocalId)
    );

    if (!door) {
      showErrorAlert(
        'Error',
        'La lectora seleccionada no es válida para este edificio.'
      );
      return;
    }

    showConfirmAlert({
      title: 'Enviar configuración Wi-Fi',
      message: `Se enviará la configuración Wi-Fi (SSID: ${ssid}) a la maestra de ${door.name || 'Lectora'} (ID: ${selectedLocalId}).`,
      cancelButtonText: 'Cancelar',
      confirmText: 'Enviar',
      onConfirm: () => {
        setSending(true);
        emitReaderConfig({
          buildingId,
          localId: selectedLocalId,
          config: {
            target: 'master',
            wifi: {
              ssid,
            },
          },
          doorName: door.name || `Lectora ${selectedLocalId}`,
        });
      },
    });
  };

  const handleSendDeviceId = (deviceId) => {
    if (!selectedLocalId) {
      showErrorAlert('Error', 'Selecciona una lectora.');
      return;
    }

    const door = doors.find(
      (d) => String(d.localId) === String(selectedLocalId)
    );

    if (!door) {
      showErrorAlert(
        'Error',
        'La lectora seleccionada no es válida para este edificio.'
      );
      return;
    }

    showConfirmAlert({
      title: 'Cambiar ID de Lectora',
      message: `Se cambiará el ID de la lectora a ${deviceId}. El comando se enviará a la maestra de ${door.name || 'Lectora'} (ID actual: ${selectedLocalId}).`,
      cancelButtonText: 'Cancelar',
      confirmText: 'Enviar',
      onConfirm: () => {
        setSending(true);
        emitReaderConfig({
          buildingId,
          localId: selectedLocalId,
          config: {
            target: 'master',
            logic: {
              device_id: deviceId,
            },
          },
          doorName: door.name || `Lectora ${selectedLocalId}`,
        });
      },
    });
  };

  if (!buildingId) {
    return <p className="text-white">Selecciona un edificio para continuar.</p>;
  }

  return (
    <div className="mt-2">
      {selectedLocalId ? (
        <div className="mb-4">
          <UnifiedConfigCard
            masterConfig={masterConfig}
            setMasterConfig={setMasterConfig}
            slaveConfig={slaveConfig}
            setSlaveConfig={setSlaveConfig}
            onSendMaster={() => handleSend('master', masterConfig)}
            onSendSlave={() => handleSend('slave', slaveConfig)}
            onSendWifi={handleSendWifi}
            onSendDeviceId={handleSendDeviceId}
            sending={sending}
            selectedLocalId={selectedLocalId}
          />
        </div>
      ) : (
        <p className="text-white">Selecciona una lectora para habilitar la configuración.</p>
      )}
    </div>
  );
};

ReaderConfig.propTypes = {
  buildingId: PropTypes.string,
  doors: PropTypes.array,
  selectedLocalId: PropTypes.string.isRequired,
  setSelectedLocalId: PropTypes.func.isRequired,
};

export default ReaderConfig;
